// Administration → Identity: per-org SSO (SAML / OIDC) + SCIM provisioning,
// and Administration → Audit: export / daily archives. Thin UI over
// backend/api/enterpriseApi.js — the org doc's `sso` / `scim` maps are
// server-written; this component only reads them and calls the admin routes.
import React, { useEffect, useState } from 'react';
import ModuleFrame, { KV } from './ModuleFrame';
import { apiGet, apiPost, apiDelete, backendBase, isBackendConfigured } from '../utils/backendApi';
import { auth } from '../firebaseConfig';

async function apiPut(path, body) {
  const user = auth.currentUser;
  const token = await user.getIdToken();
  const res = await fetch(`${backendBase()}${path}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

export function SsoPanels({ org, isAdmin }) {
  const [state, setState] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ kind: 'saml', displayName: '', domains: '', enforced: false, idpEntityId: '', ssoUrl: '', certificate: '', issuer: '', clientId: '', clientSecret: '' });
  const [scimToken, setScimToken] = useState('');

  const load = async () => {
    if (!isBackendConfigured()) return;
    try { setState(await apiGet(`/sso/${org.id}`)); } catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [org.id, org.sso?.updatedAt, org.scim?.issuedAt]);

  const startEdit = () => {
    const s = org.sso || {};
    setForm((f) => ({ ...f, kind: s.kind || 'saml', displayName: s.displayName || '', domains: (s.domains || []).join(', '), enforced: s.enforced === true }));
    setEditing(true);
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try { await apiPut(`/sso/${org.id}`, form); setEditing(false); await load(); } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Remove SSO for this organization? Members fall back to email + password.')) return;
    setBusy(true); setErr('');
    try { await apiDelete(`/sso/${org.id}`); await load(); } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  const mintScim = async () => {
    if (org.scim?.tokenHash && !window.confirm('Rotate the SCIM token? The IdP must be updated with the new one.')) return;
    setBusy(true); setErr('');
    try { const out = await apiPost(`/scim-token/${org.id}`); setScimToken(out.token); await load(); } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  const revokeScim = async () => {
    if (!window.confirm('Revoke the SCIM token? Provisioning from the IdP stops immediately.')) return;
    setBusy(true); setErr('');
    try { await apiDelete(`/scim-token/${org.id}`); setScimToken(''); await load(); } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };

  const sso = org.sso || null;
  const sp = state?.sp;
  const field = (label, key, props = {}) => (
    <div className="cs-field">
      <label>{label}</label>
      {props.textarea
        ? <textarea className="cs-input" rows={4} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...props} />
        : <input className="cs-input" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...props} />}
    </div>
  );

  return (
    <>
      <ModuleFrame.Panel
        title="Single sign-on"
        meta={sso ? `${sso.kind.toUpperCase()} · ${sso.enforced ? 'enforced' : 'optional'}` : 'not configured'}
        actions={isAdmin && !editing && (
          <>
            <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={startEdit} disabled={busy}>{sso ? 'Edit' : 'Set up SSO'}</button>
            {sso && <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={remove} disabled={busy}>Remove</button>}
          </>
        )}
      >
        {err && <p className="cs-help" style={{ color: 'var(--danger, var(--accent))' }}>{err}</p>}
        {!editing && sso && (
          <KV rows={[
            ['Provider', `${sso.displayName || 'SSO'} (${sso.kind.toUpperCase()})`],
            ['Email domains', (sso.domains || []).join(', ')],
            ['Enforced', sso.enforced ? 'yes — members of these domains must sign in through the IdP (owner keeps a break-glass path)' : 'no — "Continue with SSO" is offered, password sign-in still works'],
            ['Provider id', sso.providerId],
          ]} />
        )}
        {!editing && !sso && (
          <p className="cs-help">Connect your identity provider (Okta, Entra ID, Google Workspace, OneLogin…) via SAML 2.0 or OIDC. People sign in with "Continue with SSO" on the login page; enforce it once the IdP is verified.</p>
        )}
        {sp && !editing && (
          <>
            <p className="cs-help" style={{ marginTop: 10 }}>Service-provider values for your IdP:</p>
            <KV rows={[['Entity ID / Audience', sp.entityId], ['ACS URL / Redirect URI', sp.acsUrl], ['Name ID format', 'emailAddress — the asserted email must match the member email']]} />
          </>
        )}
        {editing && (
          <form onSubmit={save}>
            <div className="cs-field">
              <label>Protocol</label>
              <select className="cs-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                <option value="saml">SAML 2.0</option>
                <option value="oidc">OpenID Connect</option>
              </select>
            </div>
            {field('Display name', 'displayName', { placeholder: 'Okta', maxLength: 60 })}
            {field('Email domains (comma separated)', 'domains', { placeholder: 'acme.com, acme.co.uk', required: true })}
            {form.kind === 'saml' ? (
              <>
                {field('IdP entity ID', 'idpEntityId', { required: true })}
                {field('IdP SSO URL', 'ssoUrl', { type: 'url', required: true })}
                {field('IdP signing certificate (PEM)', 'certificate', { textarea: true, required: !sso, placeholder: '-----BEGIN CERTIFICATE-----' })}
              </>
            ) : (
              <>
                {field('Issuer URL', 'issuer', { type: 'url', required: true })}
                {field('Client ID', 'clientId', { required: true })}
                {field('Client secret (optional, id_token flow works without)', 'clientSecret', { type: 'password' })}
              </>
            )}
            <label className="cs-check">
              <input type="checkbox" checked={form.enforced} onChange={(e) => setForm({ ...form, enforced: e.target.checked })} />
              <span>Enforce SSO for these domains (your own domain must be listed)</span>
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="submit" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Save SSO'}</button>
              <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
            </div>
          </form>
        )}
      </ModuleFrame.Panel>

      <ModuleFrame.Panel
        title="SCIM provisioning"
        meta={state?.scim?.enabled ? 'enabled' : 'off'}
        actions={isAdmin && (
          <>
            <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={mintScim} disabled={busy}>{org.scim?.tokenHash ? 'Rotate token' : 'Enable SCIM'}</button>
            {org.scim?.tokenHash && <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={revokeScim} disabled={busy}>Revoke</button>}
          </>
        )}
      >
        <p className="cs-help">Let the IdP create, update and deactivate members (SCIM 2.0, Users resource; roles via the <code>roles</code> attribute: <code>admin</code> or <code>member</code>). Deactivated users lose their membership immediately.</p>
        {state?.scim?.baseUrl && <KV rows={[['SCIM base URL', state.scim.baseUrl], ['Auth', 'HTTP header: Authorization: Bearer <token>'], ['Token issued', state.scim.tokenIssuedAt ? new Date(state.scim.tokenIssuedAt).toLocaleString() : '—']]} />}
        {scimToken && (
          <div className="cs-empty" style={{ padding: 12, marginTop: 10 }}>
            <strong>Copy this token now — it is shown once.</strong>
            <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5, marginTop: 6, overflowWrap: 'anywhere' }}>{scimToken}</div>
          </div>
        )}
      </ModuleFrame.Panel>
    </>
  );
}

// Company email domains (backend/api/orgsApi.js, shared/signupPolicy.js).
// A domain is PROVEN with a DNS TXT record; only then does signup discovery
// route colleagues here ("Acme already uses nventr → request to join"),
// may SSO ride on it, and does the join-request rule accept the domain.
// The org doc's `domains` / `verifiedDomains` are server-written.
export function DomainsPanel({ org, isAdmin }) {
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const domains = Object.entries(org.domains || {}).sort(([a], [b]) => a.localeCompare(b));
  const act = async (key, fn) => {
    setBusy(key); setErr(''); setNote('');
    try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const add = (e) => {
    e.preventDefault();
    const d = domain.trim().toLowerCase();
    if (!d) return;
    act('add', async () => { await apiPost(`/orgs/${org.id}/domains`, { domain: d }); setDomain(''); setNote(`Added ${d} — publish the TXT record below, then Verify.`); });
  };
  const verify = (d) => act(`verify-${d}`, async () => {
    try { await apiPost(`/orgs/${org.id}/domains/${encodeURIComponent(d)}/verify`); setNote(`${d} verified. Colleagues who sign up with @${d} now find this organization and can request to join.`); } catch (e) { throw new Error(e.message); }
  });
  const remove = (d) => {
    if (!window.confirm(`Remove ${d}? Colleagues on @${d} will no longer find this organization at signup, and SSO on it stops being discoverable.`)) return;
    act(`remove-${d}`, () => apiDelete(`/orgs/${org.id}/domains/${encodeURIComponent(d)}`));
  };
  const policy = (d, patch) => act(`policy-${d}`, () => apiPut(`/orgs/${org.id}/domains/${encodeURIComponent(d)}`, patch));

  return (
    <ModuleFrame.Panel title="Domains" meta={`${(org.verifiedDomains || []).length} verified`}>
      <p className="cs-help">Prove your company's email domains. Once a domain is verified, anyone who signs up with a verified address on it is routed to this organization — they request to join and an admin approves — instead of creating their own; SSO can only be enabled for verified domains.</p>
      {err && <p className="cs-help" style={{ color: 'var(--danger, var(--accent))' }}>{err}</p>}
      {note && <p className="cs-help" style={{ color: 'var(--ok, var(--text))' }}>{note}</p>}
      <ul className="mf-list" style={{ marginTop: 10 }}>
        {domains.map(([d, e]) => (
          <li key={d} className="mf-row" style={{ flexWrap: 'wrap', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ display: 'grid', gap: 4, flex: 1, minWidth: 240 }}>
              <span><strong>{d}</strong> <span className="mf-muted">· {e.status === 'verified' ? `verified ${e.verifiedAt ? new Date(e.verifiedAt).toLocaleDateString() : ''}` : 'not verified'}</span></span>
              {e.status !== 'verified' && (
                <span className="cs-help" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>
                  Add a DNS TXT record — host <code>_nventr.{d}</code>, value <code>nventr-verify={e.token}</code> — then Verify. Propagation can take a few minutes.
                </span>
              )}
              {e.status === 'verified' && isAdmin && (
                <span className="cs-help" style={{ fontSize: 12, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  <label className="cs-check" style={{ margin: 0 }}>
                    <input type="checkbox" checked={e.joinPolicy !== 'off'} disabled={!!busy} onChange={(ev) => policy(d, { joinPolicy: ev.target.checked ? 'request' : 'off' })} />
                    <span>Colleagues may request to join</span>
                  </label>
                  <label className="cs-check" style={{ margin: 0 }}>
                    <input type="checkbox" checked={e.allowOtherOrgs === true} disabled={!!busy} onChange={(ev) => policy(d, { allowOtherOrgs: ev.target.checked })} />
                    <span>Allow separate organizations on this domain</span>
                  </label>
                </span>
              )}
            </div>
            {isAdmin && (
              <span style={{ display: 'flex', gap: 6 }}>
                {e.status !== 'verified' && <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={!!busy} onClick={() => verify(d)}>{busy === `verify-${d}` ? 'Checking…' : 'Verify'}</button>}
                <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!!busy} onClick={() => remove(d)} data-agent-noclick>Remove</button>
              </span>
            )}
          </li>
        ))}
        {!domains.length && <li className="mf-muted">no domains yet</li>}
      </ul>
      {isAdmin && (
        <form onSubmit={add} style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input className="cs-input" style={{ flex: 1 }} placeholder="acme.com" value={domain} onChange={(e) => setDomain(e.target.value)} aria-label="Domain to add" />
          <button type="submit" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!domain.trim() || !!busy}>{busy === 'add' ? 'Adding…' : 'Add domain'}</button>
        </form>
      )}
    </ModuleFrame.Panel>
  );
}

export function AuditExport({ org, isAdmin }) {
  const [archives, setArchives] = useState(null);
  const [err, setErr] = useState('');
  const [range, setRange] = useState({ from: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) });
  const [open, setOpen] = useState(false);

  const download = async (format) => {
    setErr('');
    try {
      const token = await auth.currentUser.getIdToken();
      const to = Date.parse(`${range.to}T23:59:59.999Z`);
      const from = Date.parse(`${range.from}T00:00:00Z`);
      const res = await fetch(`${backendBase()}/audit/${org.id}/export?from=${from}&to=${to}&format=${format}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || `HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `audit-${org.id}-${range.from}_${range.to}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setErr(e.message); }
  };
  const loadArchives = async () => {
    setErr('');
    try { setArchives((await apiGet(`/audit/${org.id}/archives`)).archives || []); } catch (e) { setErr(e.message); }
  };

  if (!isAdmin || !isBackendConfigured()) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
      <input type="date" className="cs-input" style={{ width: 150 }} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} aria-label="From" />
      <span className="mf-muted">→</span>
      <input type="date" className="cs-input" style={{ width: 150 }} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} aria-label="To" />
      <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => download('csv')}>Export CSV</button>
      <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => download('ndjson')}>NDJSON</button>
      <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setOpen(!open); if (!open && archives === null) loadArchives(); }}>Daily archives</button>
      {err && <span className="cs-help" style={{ color: 'var(--danger, var(--accent))' }}>{err}</span>}
      {open && (
        <div style={{ flexBasis: '100%' }}>
          {archives === null && <p className="cs-help">Loading…</p>}
          {archives && !archives.length && <p className="cs-help">No daily archives yet — one NDJSON file per day with activity is written each night.</p>}
          {archives && archives.length > 0 && (
            <ul className="cs-help" style={{ columns: 3, margin: '6px 0' }}>
              {archives.map((a) => <li key={a.day}><a href={a.url} target="_blank" rel="noreferrer">{a.day}</a> <span className="mf-muted">({Math.round(a.bytes / 1024) || 1} KB)</span></li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
